import { Pause, Square } from "lucide-react";
import { useState } from "react";
import { useSelector } from "react-redux";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { ExperienceFe } from "@intelligems/ig-types";
import { Stack } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useEndExperience } from "@src/api/experience-actions/end-experience";
import { usePauseExperience } from "@src/api/experience-actions/pause-experience";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { selectedVariations } from "@src/store/slices/variations";
import { handleSwitchToDesktop } from "@src/utils/deviceDetection";

import { MobileEndTestModal } from "../modals/MobileEndTestModal";
import { MobileGoToDesktopModal } from "../modals/MobileGoToDesktopModal";
import { MobilePriceActionModal } from "../modals/MobilePriceActionModal";
import { MobileThemeAcknowledgementModal } from "../modals/MobileThemeAcknowledgementModal";

type Props = {
  experience: ExperienceFe;
  onClose?: () => void;
};

export const MoreActionsDrawerContent = (props: Props) => {
  const { experience, onClose } = props;

  const pauseExperience = usePauseExperience(experience.category);
  const endExperience = useEndExperience({
    experienceId: experience.id,
    category: experience.category,
  });

  // Close drawer on successful mutations
  const handleMutationSuccess = () => {
    onClose?.();
  };

  const variations = useSelector(selectedVariations);
  const isAdmin = useIsAdmin();
  const downloadPriceCSVEnabled = useConfiguration("enable_price_csv_download");
  const enableNotUpdatingPrices = useConfiguration(
    "enable_not_updating_prices"
  );

  // Modal states
  const [goToDesktopReason, setGoToDesktopReason] = useState<
    "multi-currency" | "duplicate-products" | null
  >(null);
  const [showEndTestModal, setShowEndTestModal] = useState(false);
  const [showPausePriceModal, setShowPausePriceModal] = useState(false);
  const [showEndPriceModal, setShowEndPriceModal] = useState(false);
  const [showThemeAcknowledgement, setShowThemeAcknowledgement] =
    useState(false);
  const [themeAction, setThemeAction] = useState<"pause" | "end">("pause");
  const [pendingVariationId, setPendingVariationId] = useState<string | null>(
    null
  );
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  );

  if (experience?.id === undefined) {
    return null;
  }

  const isLoading = pauseExperience.isPending || endExperience.isPending;

  const isPriceTest = experience.testTypes?.hasTestPricing ?? false;
  const isThemeTest = experience.type === "content/theme";
  const isShippingTest = experience.type === "shipping";
  const allowManualPriceUpdate = !!(
    isAdmin ||
    downloadPriceCSVEnabled?.value ||
    enableNotUpdatingPrices?.value
  );

  // Pause test handler
  const handlePause = () => {
    // Check for multi-currency
    if (
      experience.currency?.code &&
      storeCurrencyConfig?.value?.code !== experience.currency?.code
    ) {
      setThemeAction("pause");
      setGoToDesktopReason("multi-currency");
      return;
    }

    // Check for duplicate products enabled
    if (shouldDuplicateProducts?.value && isPriceTest) {
      setThemeAction("pause");
      setGoToDesktopReason("duplicate-products");
      return;
    }

    // Check if price test
    if (isPriceTest) {
      setShowPausePriceModal(true);
      return;
    }

    // Check if theme test - show acknowledgement BEFORE pausing
    if (isThemeTest) {
      setThemeAction("pause");
      setShowThemeAcknowledgement(true);
      return;
    }

    // Direct pause for other tests
    pauseExperience.mutate(
      {
        body: { variationId: undefined },
        queryParams: undefined,
        pathParams: {
          experienceId: experience.id!,
          name: experience.name,
          action: "pause",
        },
      },
      {
        onSuccess: handleMutationSuccess,
      }
    );
  };

  // End test handler
  const handleEnd = () => {
    // Check for multi-currency
    if (
      experience.currency?.code &&
      storeCurrencyConfig?.value?.code !== experience.currency?.code
    ) {
      setThemeAction("end");
      setGoToDesktopReason("multi-currency");
      return;
    }

    // Check for duplicate products enabled
    if (shouldDuplicateProducts?.value && isPriceTest) {
      setThemeAction("end");
      setGoToDesktopReason("duplicate-products");
      return;
    }

    // Check if price test
    if (isPriceTest) {
      setShowEndPriceModal(true);
      return;
    }

    // For other tests, show the standard end modal
    setShowEndTestModal(true);
  };

  // End test confirmation (from MobileEndTestModal)
  const handleConfirmEndTest = () => {
    setShowEndTestModal(false);

    // Check if theme test - show acknowledgement after ending
    if (isThemeTest) {
      setThemeAction("end");
      // We'll end first, then show acknowledgement
    }

    endExperience.mutate(
      {
        body: { variationId: undefined },
        queryParams: undefined,
        pathParams: {
          experienceId: experience.id!,
          name: experience.name,
          action: "end",
        },
      },
      {
        onSuccess: () => {
          // Show theme acknowledgement or close drawer
          if (isThemeTest) {
            setTimeout(() => {
              setShowThemeAcknowledgement(true);
            }, 500);
          } else {
            handleMutationSuccess();
          }
        },
      }
    );
  };

  // Price test pause confirmation
  const handlePausePriceTest = (variationId: string | null) => {
    setShowPausePriceModal(false);

    // If theme test, show acknowledgement modal BEFORE pausing
    if (isThemeTest) {
      setPendingVariationId(variationId);
      setThemeAction("pause");
      setShowThemeAcknowledgement(true);
      // The actual pause will happen in handleThemeAcknowledgement
      return;
    }

    // For non-theme price tests, pause directly
    pauseExperience.mutate(
      {
        body: {
          variationId,
          updatePricesToVariation: variationId !== null,
        },
        queryParams: undefined,
        pathParams: {
          experienceId: experience.id!,
          name: experience.name,
          action: "pause",
        },
      },
      {
        onSuccess: handleMutationSuccess,
      }
    );
  };

  // Price test end confirmation
  const handleEndPriceTest = (variationId: string | null) => {
    setShowEndPriceModal(false);

    // If theme test, show acknowledgement modal after price selection
    if (isThemeTest) {
      setThemeAction("end");
      setShowThemeAcknowledgement(true);
      // Store variationId for later use
    }

    endExperience.mutate(
      {
        body: {
          variationId,
          updatePricesToVariation: variationId !== null,
        },
        queryParams: undefined,
        pathParams: {
          experienceId: experience.id!,
          name: experience.name,
          action: "end",
        },
      },
      {
        onSuccess: () => {
          // Show theme acknowledgement or close drawer
          if (isThemeTest) {
            setTimeout(() => {
              setShowThemeAcknowledgement(true);
            }, 500);
          } else {
            handleMutationSuccess();
          }
        },
      }
    );
  };

  // Theme acknowledgement confirmation
  const handleThemeAcknowledgement = () => {
    setShowThemeAcknowledgement(false);

    // Execute the pending action after acknowledgement
    if (themeAction === "pause") {
      pauseExperience.mutate(
        {
          body: {
            variationId: pendingVariationId ?? undefined,
            updatePricesToVariation:
              pendingVariationId !== null && pendingVariationId !== undefined,
          },
          queryParams: undefined,
          pathParams: {
            experienceId: experience.id!,
            name: experience.name,
            action: "pause",
          },
        },
        {
          onSuccess: handleMutationSuccess,
        }
      );
      setPendingVariationId(null);
    } else {
      // For "end" action, the API call is already made, just close the drawer
      handleMutationSuccess();
    }
  };

  const buttonStyle = {
    color: "white !important",
    "&:disabled": {
      color: "white !important",
    },
  } as const;

  const actionButtons = {
    started: (
      <Stack spacing={2}>
        <IgButton
          onClick={handlePause}
          disabled={isLoading}
          startIcon={<Pause size={16} color="white" />}
          fullWidth
          sx={buttonStyle}
        >
          Pause Test
        </IgButton>
        <IgButton
          onClick={handleEnd}
          disabled={isLoading}
          startIcon={<Square size={16} color="white" />}
          fullWidth
          sx={buttonStyle}
          color="error"
        >
          End Test
        </IgButton>
      </Stack>
    ),
    paused: (
      <IgButton
        onClick={handleEnd}
        disabled={isLoading}
        loading={isLoading}
        startIcon={<Square size={16} color="white" />}
        fullWidth
        sx={buttonStyle}
        color="error"
      >
        End Test
      </IgButton>
    ),
    ended: (
      <Stack>
        <IgTypography>
          This test has ended. You can no longer make changes to it.
        </IgTypography>
      </Stack>
    ),
    pending: (
      <Stack>
        <IgTypography>
          You cannot start pending tests from the mobile app. Please switch to
          the desktop app.
        </IgTypography>
      </Stack>
    ),
  } as const;

  return (
    <>
      <Stack spacing={3} sx={{ py: 2 }}>
        {actionButtons[experience.status]}
      </Stack>

      {/* Modals */}
      <MobileEndTestModal
        open={showEndTestModal}
        onClose={() => setShowEndTestModal(false)}
        onEnd={handleConfirmEndTest}
        experience={experience}
        loading={isLoading}
      />

      <MobilePriceActionModal
        open={showPausePriceModal}
        onClose={() => setShowPausePriceModal(false)}
        onConfirm={handlePausePriceTest}
        variations={variations}
        action="pause"
        loading={isLoading}
        allowSkipUpdate={allowManualPriceUpdate}
      />

      <MobilePriceActionModal
        open={showEndPriceModal}
        onClose={() => setShowEndPriceModal(false)}
        onConfirm={handleEndPriceTest}
        variations={variations}
        action="end"
        loading={isLoading}
        allowSkipUpdate={allowManualPriceUpdate}
      />

      <MobileThemeAcknowledgementModal
        open={showThemeAcknowledgement}
        onClose={() => setShowThemeAcknowledgement(false)}
        onAcknowledge={handleThemeAcknowledgement}
        action={themeAction}
        loading={isLoading}
      />

      {goToDesktopReason && (
        <MobileGoToDesktopModal
          open={true}
          onClose={() => setGoToDesktopReason(null)}
          onAcknowledge={() => handleSwitchToDesktop(experience)}
          action={themeAction}
          reason={goToDesktopReason}
          loading={isLoading}
        />
      )}
    </>
  );
};
